import { useMemo, useState } from "react";
import {
  DndContext, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, closestCorners,
  useDroppable, useSensor, useSensors, type DragEndEvent, type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Plus, X } from "lucide-react";
import { displayName, type BoardRow, type Card, type ColumnRow, type Label, type Person } from "@/lib/board/types";
import { CardTile } from "./CardTile";

const COLUMN_DROP = "col:";

export interface ColumnView {
  column: ColumnRow;
  /** Visible cards, filtered and sorted. */
  cards: Card[];
  /** Every card in the column, for the "3 of 7" count while filtering. */
  total: number;
}

/**
 * The columns, with drag and drop that works on phones.
 *
 * Mouse drags start after 6px of movement, so a click still opens the card.
 * Touch drags start after a 200ms press, so a swipe still scrolls the board
 * sideways. Space or Enter picks a card up from the keyboard.
 */
export function BoardColumns({
  board, views, filtering, allowCreate = true, peopleById, assigneesByCard, labelsByCard, members, selectedPeople, onOpen, onCreate, onMove,
}: {
  board: BoardRow;
  views: ColumnView[];
  filtering: boolean;
  /** The archive is for reading cards that are already done, not for adding new ones. */
  allowCreate?: boolean;
  peopleById: Map<string, Person>;
  assigneesByCard: Map<string, string[]>;
  labelsByCard: Map<string, Label[]>;
  onOpen: (card: Card) => void;
  /** Everyone who can be put on a card. */
  members: Person[];
  /** People picked at the top of the board: a new card starts out assigned to them. */
  selectedPeople: string[];
  onCreate: (columnId: string, title: string, assigneeIds: string[]) => void;
  onMove: (card: Card, columnId: string, index: number) => void;
}) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [activeId, setActiveId] = useState<string | null>(null);

  const cardById = useMemo(() => {
    const m = new Map<string, Card>();
    for (const v of views) for (const c of v.cards) m.set(c.id, c);
    return m;
  }, [views]);

  function onDragStart(e: DragStartEvent) {
    setActiveId(String(e.active.id));
  }

  function onDragEnd(e: DragEndEvent) {
    setActiveId(null);
    const card = cardById.get(String(e.active.id));
    if (!card || !e.over) return;
    const overId = String(e.over.id);
    if (overId.startsWith(COLUMN_DROP)) {
      const columnId = overId.slice(COLUMN_DROP.length);
      const view = views.find(v => v.column.id === columnId);
      if (!view) return;
      const index = view.cards.filter(c => c.id !== card.id).length;
      if (columnId === card.column_id && view.cards[view.cards.length - 1]?.id === card.id) return;
      onMove(card, columnId, index);
      return;
    }
    const overCard = cardById.get(overId);
    if (!overCard || overCard.id === card.id) return;
    const view = views.find(v => v.column.id === overCard.column_id);
    if (!view) return;
    onMove(card, overCard.column_id, view.cards.findIndex(c => c.id === overCard.id));
  }

  const active = activeId ? cardById.get(activeId) : undefined;
  const tile = (card: Card, dragging = false) => (
    <CardTile
      board={board}
      card={card}
      assignees={(assigneesByCard.get(card.id) ?? []).map(id => peopleById.get(id)).filter((p): p is Person => !!p)}
      labels={labelsByCard.get(card.id) ?? []}
      creator={card.created_by ? peopleById.get(card.created_by) : undefined}
      done={!!views.find(v => v.column.id === card.column_id)?.column.is_done}
      onOpen={() => onOpen(card)}
      dragging={dragging}
    />
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <div className="-mx-5 snap-x snap-mandatory overflow-x-auto px-5 md:-mx-8 md:snap-none md:px-8">
        <div className="flex gap-3 pb-4" style={{ minWidth: "min-content" }}>
          {views.map(v => (
            <BoardColumn
              key={v.column.id}
              view={v}
              filtering={filtering}
              members={members}
              selectedPeople={selectedPeople}
              allowCreate={allowCreate}
              onCreate={(title, ids) => onCreate(v.column.id, title, ids)}
              renderCard={c => tile(c)}
            />
          ))}
        </div>
      </div>
      <DragOverlay>{active ? tile(active, true) : null}</DragOverlay>
    </DndContext>
  );
}

function BoardColumn({
  view, filtering, allowCreate, members, selectedPeople, onCreate, renderCard,
}: {
  view: ColumnView;
  filtering: boolean;
  allowCreate: boolean;
  members: Person[];
  selectedPeople: string[];
  onCreate: (title: string, assigneeIds: string[]) => void;
  renderCard: (c: Card) => React.ReactNode;
}) {
  const { column, cards, total } = view;
  const { setNodeRef, isOver } = useDroppable({ id: `${COLUMN_DROP}${column.id}` });
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");

  const [assignees, setAssignees] = useState<string[]>([]);
  const [search, setSearch] = useState("");

  function open() {
    // Whoever is picked at the top gets the card; with nobody picked it starts unassigned.
    setAssignees(selectedPeople);
    setSearch("");
    setAdding(true);
  }
  function close() { setAdding(false); setTitle(""); setSearch(""); }
  function submit() {
    if (title.trim()) onCreate(title.trim(), assignees);
    close();
  }
  const byId = new Map(members.map(m => [m.id, m]));
  const q = search.trim().toLowerCase();
  const matches = q
    ? members.filter(m => !assignees.includes(m.id) && `${displayName(m)} ${m.email ?? ""}`.toLowerCase().includes(q)).slice(0, 5)
    : [];

  return (
    <section
      aria-label={column.name}
      className={`flex w-[85vw] max-w-[20rem] shrink-0 snap-start flex-col rounded-xl bg-offwhite ring-1 transition sm:w-80 ${
        isOver ? "ring-2 ring-teal" : "ring-line"
      }`}
    >
      {/* Sticky: the header sits outside the scrolling card list. */}
      <header
        className={`sticky top-0 z-10 flex items-center justify-between gap-2 rounded-t-xl px-3 py-2.5 ${
          column.is_done ? "bg-ok-bg" : "bg-line/60"
        }`}
      >
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="truncate text-sm font-semibold uppercase tracking-wider">{column.name}</h3>
          <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-gray2 ring-1 ring-line" aria-label={`${cards.length} cards`}>
            {filtering ? `${cards.length}/${total}` : cards.length}
          </span>
        </div>
        {allowCreate && (
          <button
            type="button"
            onClick={open}
            className="flex min-h-11 shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-teal-dark hover:bg-white"
            aria-label={`Add a card to ${column.name}`}
          >
            <Plus size={14} aria-hidden /> Add card
          </button>
        )}
      </header>

      <div ref={setNodeRef} className="flex max-h-[calc(100dvh-300px)] min-h-[120px] flex-col gap-2 overflow-y-auto p-2">
        {adding && (
          <div className="rounded-lg bg-white p-2 ring-1 ring-teal">
            <input
              className="input"
              autoFocus
              aria-label={`New card title in ${column.name}`}
              placeholder="Card title…"
              value={title}
              onChange={e => setTitle(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") submit();
                if (e.key === "Escape") close();
              }}
            />
            <div className="mt-2">
              <div className="flex flex-wrap items-center gap-1" aria-label="Assigned to">
                <span className="text-xs text-gray2">Assigned to:</span>
                {assignees.length === 0 && <span className="text-xs text-gray2">nobody yet</span>}
                {assignees.map(id => {
                  const p = byId.get(id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1 rounded-full bg-teal-mint px-2 py-0.5 text-xs font-medium text-teal-dark">
                      {p ? displayName(p) : "Someone"}
                      <button type="button" aria-label={`Remove ${p ? displayName(p) : "this person"} from the new card`}
                        onClick={() => setAssignees(a => a.filter(x => x !== id))}>
                        <X size={12} aria-hidden />
                      </button>
                    </span>
                  );
                })}
              </div>
              <input
                className="input mt-1"
                aria-label="Search a name to add someone to the new card"
                placeholder="Add someone to work on it…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === "Escape") close(); }}
              />
              {matches.length > 0 && (
                <ul className="mt-1 overflow-hidden rounded-md ring-1 ring-line">
                  {matches.map(m => (
                    <li key={m.id}>
                      <button type="button" className="block w-full px-2 py-1 text-left text-xs hover:bg-offwhite"
                        onClick={() => { setAssignees(a => [...a, m.id]); setSearch(""); }}>
                        {displayName(m)}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {q && matches.length === 0 && <p className="mt-1 text-xs text-gray2">No one on this board matches.</p>}
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <button type="button" onClick={close} className="text-xs text-gray2 hover:text-bad">Cancel</button>
              <button type="button" onClick={submit} className="btn btn-sm">Add</button>
            </div>
          </div>
        )}
        <SortableContext items={cards.map(c => c.id)} strategy={verticalListSortingStrategy}>
          {cards.map(c => <SortableCard key={c.id} id={c.id}>{renderCard(c)}</SortableCard>)}
        </SortableContext>
        {cards.length === 0 && !adding && (
          <p className="py-6 text-center text-xs text-gray2">{filtering ? "Nothing matches here" : "No cards yet"}</p>
        )}
      </div>
    </section>
  );
}

function SortableCard({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1, touchAction: "manipulation" }}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}
